// pfs-countdown-ring.ts — brandbarer kreisförmiger Countdown-Ring.
//
// Entkoppelt RING (grafischer Fortschritt 0–100 % + eigener Timer) vom LABEL
// (Text/Format/Position). Kurze Labels (Sekunden) sitzen IM Ring; lange Formate
// (00:12:34) oder Custom-Inhalt daneben/drunter — der Ring wächst nicht.
//
// GRÖSSE ist voll dynamisch: die CSS-Custom-Property --pfs-ring-size akzeptiert
// JEDE Einheit (px/em/rem/%/vmin …) → der Consumer bestimmt die Größe selbst,
// z. B. `pfs-countdown-ring { --pfs-ring-size: 4rem }` oder `100%`. data-size ist
// nur ein px-Kürzel dafür. Da der Ring per SVG-viewBox rendert, skalieren Label
// + Strichstärke automatisch mit.
//
// Attribute (alle data-*):
//   data-remaining        Sekunden verbleibend (Start). Treibt den Countdown.
//   data-total            Sekunden = 100 % (Ring-Basis). Default = remaining.
//   data-format           FLEXIBEL — Preset ODER Token-Pattern (Default seconds):
//                           Presets:  seconds ("09s") · clock ("M:SS"/"H:MM:SS") · none
//                           Pattern:  H/HH (Std) · M/MM (Min 0–59) · S/SS (Sek 0–59)
//                                     · T/TT (Gesamt-Sekunden) · [text] = wörtlich
//                           z. B.  "HH:MM:SS" · "M:SS" · "TT[s]" · "T [Sek.]"
//   data-label-placement  inside | beside | below | none (Default inside)
//   data-size             Durchmesser in px (Kürzel für --pfs-ring-size). Optional.
//   data-paused           "" / "true" → Ticker pausiert
//   data-on-error         ignore (Default) | abort — Fehler-Policy für Callbacks:
//                           ignore = Fehler schlucken, Timer läuft weiter;
//                           abort  = Timer stoppen, pfs:countdown-error feuern UND
//                                    den Fehler weiterpropagieren.
//
// Properties (per JS gesetzt, jederzeit; werden live gelesen):
//   formatter: (remainingSeconds)=>string  — überschreibt data-format (auch inside).
//   onDone:   ()=>boolean|void|Promise<…>   — Aktion bei Ablauf (Funktion DIREKT
//             übergeben statt addEventListener). Async erlaubt (fire-and-forget).
//   onTick:   (remaining, total)=>boolean|void — pro Sekunde. RETURN true (sync
//             oder via aufgelöstes Promise) BRICHT den Countdown AB.
//   z. B.  ring.onDone = async () => { await logout(); location.assign('/login'); };
//          ring.onTick = (r) => r === 5 ? true : undefined;  // bei 5s abbrechen
// Method: ring.stop()  — Countdown imperativ abbrechen.
// Slot (default): bei placement=beside|below überschreibt eigener Inhalt das Label.
//   (inside wird als SVG-Text gerendert → geometrisch exakt zentriert, kein Slot.)
//
// Events (Alternative zu den Callbacks; bubbles + composed):
//   pfs:countdown-tick  detail {remaining:number, total:number} — pro Sekunde
//   pfs:countdown-done  — bei Erreichen von 0 (genau einmal). Consumer hängt eine
//                         BELIEBIGE Aktion daran (Logout, Redirect, Refresh …).
//   pfs:countdown-error detail {error} — bei Callback-Fehler (immer; s. data-on-error).
//
// Brandbar/dark-safe: Ring = var(--pfs-ring-color, --pfs-color-primary),
//   Track = var(--pfs-ring-track, --pfs-color-outline), Text = --pfs-color-on-surface.
//   prefers-reduced-motion → keine Sub-Sekunden-Animation.

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";
import { escapeHTML } from "../../utils/escape-html.js";

const R = 46; // Radius in viewBox-Einheiten (viewBox 0 0 100 100)
const C = 2 * Math.PI * R; // Umfang

const STYLES = `
${SHADOW_RESET_CSS}
:host {
  /* inline-flex (nicht inline-block) → keine Zeilenhöhen-/Baseline-Strut-Box
     unter dem Ring. Sonst ist die Host-Box höher als der Ring, der Ring sitzt
     oben drin, und in Flex-Zeilen (align-items:center) landet das Inside-Label
     ~4px zu hoch gegenüber Nachbarn (Buttons etc.). vertical-align:middle für
     Inline-Kontexte. */
  display: inline-flex;
  vertical-align: middle;
  --_size: var(--pfs-ring-size, 48px);
  --_stroke: var(--pfs-ring-stroke, 8);
  --_color: var(--pfs-ring-color, var(--pfs-color-primary));
  --_track: var(--pfs-ring-track, var(--pfs-color-outline));
}
.wrap {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--pfs-color-on-surface);
}
.wrap[data-placement="below"] { flex-direction: column; gap: 0.25rem; }
.ring {
  width: var(--_size);
  height: var(--_size);
  flex-shrink: 0;
}
.ring svg { display: block; width: 100%; height: 100%; }
.track { fill: none; stroke: var(--_track); stroke-width: var(--_stroke); }
.prog {
  fill: none;
  stroke: var(--_color);
  stroke-width: var(--_stroke);
  stroke-linecap: round;
  stroke-dasharray: ${C};
  stroke-dashoffset: 0;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
  transition: stroke-dashoffset 0.25s linear;
}
@media (prefers-reduced-motion: reduce) {
  .prog { transition: none; }
}
/* Inside-Label als SVG-Text → geometrisch EXAKT zentriert (dominant-baseline:
   central), size-unabhängig, unabhängig von Zeilenbox-/Font-Metrik-Rundung. */
.label-svg {
  fill: var(--pfs-color-on-surface);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.label-out {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}
::slotted(*) { font-variant-numeric: tabular-nums; }
`;

// applyPattern — Token-Pattern auf den Restwert. Tokens (longest match first):
// H/HH (Std), M/MM (Min 0–59), S/SS (Sek 0–59), T/TT (Gesamt-Sek). `[text]` =
// wörtlich; alles andere bleibt wörtlich.
function applyPattern(pattern: string, remaining: number): string {
  const t = Math.max(0, Math.ceil(remaining));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  const pad = (n: number): string => String(n).padStart(2, "0");
  const map: Record<string, string> = {
    HH: pad(h), H: String(h),
    MM: pad(m), M: String(m),
    SS: pad(s), S: String(s),
    TT: pad(t), T: String(t),
  };
  let out = "";
  for (let i = 0; i < pattern.length; ) {
    if (pattern[i] === "[") {
      const end = pattern.indexOf("]", i);
      if (end > i) {
        out += pattern.slice(i + 1, end);
        i = end + 1;
        continue;
      }
    }
    const two = pattern.slice(i, i + 2);
    if (map[two] !== undefined) {
      out += map[two];
      i += 2;
      continue;
    }
    const one = pattern[i];
    if (map[one] !== undefined) {
      out += map[one];
      i += 1;
      continue;
    }
    out += one;
    i += 1;
  }
  return out;
}
// formatLabel — Preset-Kürzel oder freies Token-Pattern. Exportiert für Tests.
export function formatLabel(format: string, remaining: number): string {
  if (format === "none") return "";
  if (format === "seconds") return applyPattern("TT[s]", remaining); // Gesamt-Sek "09s"
  if (format === "clock") {
    return Math.ceil(Math.max(0, remaining)) >= 3600
      ? applyPattern("H:MM:SS", remaining)
      : applyPattern("M:SS", remaining);
  }
  return applyPattern(format, remaining); // beliebiges Pattern
}

interface CountdownRingHost extends HTMLElement {
  formatter?: (s: number) => string;
  stop?: () => void;
  onDone?: (...a: number[]) => unknown;
  onTick?: (...a: number[]) => unknown;
}

defineComponent({
  tagName: "pfs-countdown-ring",
  mixins: [withShadow()],
  render(host, ctx) {
    if (!ctx.shadowRoot) return;
    const root = ctx.shadowRoot;
    const self = host as CountdownRingHost;

    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const remaining0 = parseFloat(host.dataset.remaining ?? "0") || 0;
    const total = parseFloat(host.dataset.total ?? "") || remaining0 || 1;
    const format = host.dataset.format ?? "seconds";
    const placement = host.dataset.labelPlacement ?? "inside";
    // data-size ist ein px-Kürzel für --pfs-ring-size; ohne data-size greift die
    // CSS-Custom-Property (beliebige Einheit, vom Consumer gesetzt) bzw. 48px.
    if (host.dataset.size) {
      host.style.setProperty("--pfs-ring-size", `${parseFloat(host.dataset.size)}px`);
    }

    const labelInside = placement === "inside";
    const labelOutside = placement === "beside" || placement === "below";
    const showLabel = placement !== "none";

    // resolveLabel — formatter-Property (JS) schlägt data-format.
    const resolveLabel = (rem: number): string => {
      const fn = self.formatter;
      if (typeof fn === "function") {
        try {
          return String(fn(Math.max(0, Math.ceil(rem))));
        } catch {
          /* Formatter-Fehler → Fallback aufs Pattern */
        }
      }
      return formatLabel(format, rem);
    };
    const initLabel = escapeHTML(resolveLabel(remaining0));

    const wrap = document.createElement("div");
    wrap.className = "wrap";
    wrap.setAttribute("data-placement", placement);
    wrap.innerHTML = `
      <div class="ring">
        <svg viewBox="0 0 100 100">
          <circle class="track" cx="50" cy="50" r="${R}"></circle>
          <circle class="prog" cx="50" cy="50" r="${R}"></circle>
          ${
            labelInside && showLabel
              ? `<text class="label-svg" part="label" x="50" y="50" text-anchor="middle" dominant-baseline="central" font-size="30">${initLabel}</text>`
              : ""
          }
        </svg>
      </div>
      ${labelOutside && showLabel ? `<span class="label-out" part="label"><slot>${initLabel}</slot></span>` : ""}
    `;
    host.setAttribute("role", host.getAttribute("role") ?? "timer");
    root.appendChild(wrap);

    const prog = wrap.querySelector<SVGCircleElement>(".prog");
    const svgText = wrap.querySelector<SVGTextElement>(".label-svg");
    const outSlot = wrap.querySelector<HTMLElement>(".label-out slot");
    // Slot-Inhalt vom Consumer (nur beside/below)? Dann Label nicht überschreiben.
    const hasSlotted = [...host.childNodes].some(
      (n) => n.nodeType === 1 || (n.textContent ?? "").trim(),
    );

    const draw = (rem: number): void => {
      const frac = Math.max(0, Math.min(1, rem / total));
      if (prog) prog.style.strokeDashoffset = String(C * (1 - frac));
    };
    const setLabel = (rem: number): void => {
      if (!showLabel || hasSlotted) return;
      const txt = resolveLabel(rem);
      if (svgText) svgText.textContent = txt;
      if (outSlot) outSlot.textContent = txt;
    };

    // ── Steuerung: Abbruch + Fehler-Policy (konfigurierbar) ──
    let rafId = 0;
    let stopped = false;
    const errorPolicy = host.dataset.onError === "abort" ? "abort" : "ignore";
    const abort = (): void => {
      stopped = true;
      cancelAnimationFrame(rafId);
    };
    // stop() — imperatives Abbrechen von außen (ring.stop()).
    self.stop = abort;

    // onCbError — Fehler-Policy: ignore (schlucken, weiterlaufen) ODER abort
    // (Timer stoppen, pfs:countdown-error feuern UND Fehler weiterpropagieren).
    const onCbError = (e: unknown): void => {
      host.dispatchEvent(
        new CustomEvent("pfs:countdown-error", { detail: { error: e }, bubbles: true, composed: true }),
      );
      if (errorPolicy === "abort") {
        abort();
        throw e; // weiterpropagieren: uncaught (sync) bzw. unhandled rejection (async)
      }
    };

    // callCb — ruft eine Callback-Property auf (live gelesen → jederzeit setzbar).
    // Rückgabe === true (sync ODER via aufgelöstes Promise) bricht den Timer ab.
    // Async-Callbacks laufen eigenständig (fire-and-forget); Fehler/Reject per Policy.
    const callCb = (name: "onDone" | "onTick", ...args: number[]): void => {
      const fn = self[name];
      if (typeof fn !== "function") return;
      let r: unknown;
      try {
        r = fn(...args);
      } catch (e) {
        onCbError(e);
        return;
      }
      if (r === true) {
        abort();
        return;
      }
      if (r && typeof (r as { then?: unknown }).then === "function") {
        void (r as Promise<unknown>).then(
          (v) => {
            if (v === true) abort();
          },
          (e) => onCbError(e),
        );
      }
    };

    // ── Countdown-Loop (rAF, deadline-basiert → drift-/tab-sleep-fest) ──
    let lastWhole = -1;
    let done = false;
    const paused = host.dataset.paused === "" || host.dataset.paused === "true";
    const deadline = performance.now() + remaining0 * 1000;

    draw(remaining0);

    const tick = (): void => {
      if (stopped) return;
      const rem = Math.max(0, (deadline - performance.now()) / 1000);
      draw(rem);
      const whole = Math.ceil(rem);
      if (whole !== lastWhole) {
        lastWhole = whole;
        setLabel(rem);
        host.dispatchEvent(
          new CustomEvent("pfs:countdown-tick", {
            detail: { remaining: whole, total },
            bubbles: true,
            composed: true,
          }),
        );
        callCb("onTick", whole, total); // kann via Return===true oder Fehler abbrechen
      }
      if (stopped) return;
      if (rem <= 0) {
        if (!done) {
          done = true;
          host.dispatchEvent(new CustomEvent("pfs:countdown-done", { bubbles: true, composed: true }));
          callCb("onDone");
        }
        return;
      }
      rafId = requestAnimationFrame(tick);
    };

    if (!paused && remaining0 > 0) rafId = requestAnimationFrame(tick);
    ctx.cleanup.push(() => cancelAnimationFrame(rafId));
  },
});
